import React, { useState } from 'react';
import { CellProps, WithClassname } from '@jsonforms/core';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Close from '@mui/icons-material/Close';
import { useTheme, styled, Theme } from '@mui/material/styles';
import { InputBaseComponentProps } from '@mui/material/InputBase';
import OutlinedInput, { OutlinedInputProps } from '@mui/material/OutlinedInput';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import { withStyles } from '@mui/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FormHelperText from '@mui/material/FormHelperText';

const ERROR_COLOR = '#f21b1bd4';
const WARNING_COLOR = '#ffc107';

const StyledErrorIcon = styled(ErrorOutlineIcon)({
  color: ERROR_COLOR,
  margin: '10px 10px',
  marginLeft: '-3px',
  height: 'auto',
});
const StyledWarningIcon = styled(ErrorOutlineIcon)({
  color: WARNING_COLOR,
  margin: '10px 10px',
  marginLeft: '-3px',
  height: 'auto',
});

const warningInputSx = {
  '&.showInputWarningBorder .MuiOutlinedInput-notchedOutline': {
    borderColor: `${WARNING_COLOR} !important`,
    borderWidth: '2px',
    backgroundColor: 'inherit',
  },
};

const ParentDiv = styled('div')(() => ({
  textAlign: 'right',
  position: 'absolute',
  right: 0,
  display: 'flex',
}));

const errorTooltipSx = {
  '& .MuiTooltip-tooltip': {
    color: ERROR_COLOR,
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    boxShadow: '-1px 4px 5px 0px #969696',
    padding: '10px',
    fontSize: '0.8rem',
    bottom: '10px',
  },
  '& .MuiTooltip-arrow': {
    fontSize: '25px',
    color: '#fff',
    '&::before': {
      background: '#fff',
      border: '1px solid #ddd',
    },
  },
};

const WarningTooltip = withStyles({
  tooltip: {
    color: '#000',
    backgroundColor: '#ffc107',
    border: '1px solid #ddd',
    boxShadow: '-1px 4px 5px 0px #969696',
    padding: '10px',
    fontSize: '0.8rem',
    bottom: '10px',
  },
  arrow: {
    fontSize: '25px',
    color: '#ffc107',
    '&::before': {
      background: '#ffc107',
      border: '1px solid #ddd',
    },
  },
})(Tooltip) as React.ComponentType<TooltipProps>;

interface MuiTextInputProps {
  muiInputProps?: OutlinedInputProps['inputProps'];
  inputComponent?: OutlinedInputProps['inputComponent'];
  label?: string;
}

interface JsonFormsTheme extends Theme {
  jsonforms?: {
    input?: {
      delete?: {
        background?: string;
      };
    };
  };
}

export const MuiInputText = React.memo(
  (props: CellProps & WithClassname & MuiTextInputProps) => {
    const [showAdornment, setShowAdornment] = useState(false);
    const [showErrorHelpers, setShowErrorHelpers] = useState(false);

    const {
      data,
      config,
      className,
      id,
      enabled,
      uischema,
      isValid,
      path,
      handleChange,
      schema,
      muiInputProps,
      inputComponent,
      errors,
      label,
    } = props;

    const maxLength = schema.maxLength;
    const appliedUiSchemaOptions = { ...config, ...uischema.options };

    let inputProps: InputBaseComponentProps;
    if (appliedUiSchemaOptions.restrict) {
      inputProps = { maxLength: maxLength };
    } else {
      inputProps = {};
    }

    inputProps = { ...inputProps, ...muiInputProps };

    if (appliedUiSchemaOptions.trim && maxLength !== undefined) {
      inputProps.size = maxLength;
    }

    const onChange = (ev: any) => {
      handleChange(path, ev.target.value);
      setShowErrorHelpers(true);
    };

    const theme: JsonFormsTheme = useTheme();
    const inputDeleteBackgroundColor =
      theme.jsonforms?.input?.delete?.background ||
      theme.palette.background.default;

    const onBlurHandler = () => {
      setShowErrorHelpers(true);
    };

    const showErrorHelperText =
      !isValid &&
      errors &&
      appliedUiSchemaOptions.showErrorHelperText &&
      showErrorHelpers
        ? true
        : false;

    const showToolTip = !isValid && errors && showErrorHelpers ? true : false;

    const warningHelperText =
      data && errors && errors === appliedUiSchemaOptions.errorMessage
        ? errors
        : '';

    return (
      <>
        <OutlinedInput
          sx={{
            ...(warningHelperText ? warningInputSx : {}),
            '&.Mui-disabled': {
              opacity: 1,
              WebkitTextFillColor: 'rgba(0,0,0,.87) !important',
              color: 'rgba(0,0,0,.87) !important',
            },
            '& input.Mui-disabled': {
              opacity: 1,
              WebkitTextFillColor: 'rgba(0,0,0,.87) !important',
              color: 'rgba(0,0,0,.87) !important',
            },
            ...(warningHelperText
              ? {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${WARNING_COLOR} !important`,
                  },
                  '& .MuiInputLabel-root': {
                    color: `${WARNING_COLOR} !important`,
                  },
                }
              : {}),
          }}
          type={
            appliedUiSchemaOptions.format === 'password' ? 'password' : 'text'
          }
          value={data || ''}
          label={label}
          onChange={onChange}
          className={`${className} ${
            showErrorHelpers ? 'showInputErrorBorder' : ''
          } ${warningHelperText ? 'showInputWarningBorder' : ''}`}
          id={id}
          disabled={!enabled}
          multiline={Boolean(appliedUiSchemaOptions.multi)}
          minRows={appliedUiSchemaOptions.rowsMin}
          maxRows={appliedUiSchemaOptions.rowsMax}
          fullWidth={!appliedUiSchemaOptions.trim || maxLength === undefined}
          inputProps={inputProps}
          data-testid={`${id}-test`}
          error={!isValid && !warningHelperText}
          onPointerEnter={() => setShowAdornment(true)}
          onPointerLeave={() => setShowAdornment(false)}
          onBlur={onBlurHandler}
          endAdornment={
            <ParentDiv>
              <InputAdornment
                position="end"
                style={{
                  display:
                    !showAdornment || !enabled || !data ? 'none' : 'flex',
                  height: 'auto',
                  marginTop: '5px',
                }}
              >
                <IconButton
                  aria-label="Clear input field"
                  onClick={() => {
                    handleChange(path, '');
                    setShowErrorHelpers(true);
                  }}
                >
                  <Close
                    style={{
                      background: inputDeleteBackgroundColor,
                      borderRadius: '50%',
                    }}
                  />
                </IconButton>
              </InputAdornment>

              {warningHelperText ? (
                <WarningTooltip title={errors} placement="top" arrow>
                  <StyledWarningIcon
                    aria-label="WarningOutlineIcon"
                    className={`${showToolTip ? '' : 'hide'} warningIcon`}
                    style={{ color: WARNING_COLOR }}
                  />
                </WarningTooltip>
              ) : (
                !isValid &&
                errors && (
                  <Tooltip
                    title={errors}
                    placement="top"
                    arrow
                    sx={errorTooltipSx}
                  >
                    <StyledErrorIcon
                      aria-label="ErrorOutlineIcon"
                      className={`${showToolTip ? '' : 'hide'} errorIcon`}
                    />
                  </Tooltip>
                )
              )}
            </ParentDiv>
          }
          inputComponent={inputComponent}
        />

        {warningHelperText ? (
          <FormHelperText
            sx={{
              color: WARNING_COLOR,
              textAlign: 'left',
              marginLeft: 0,
              width: '100%',
              display: 'block',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
            }}
          >
            {warningHelperText}
          </FormHelperText>
        ) : (
          showErrorHelperText && (
            <FormHelperText
              error
              sx={{
                color: ERROR_COLOR,
                textAlign: 'left',
                marginLeft: 0,
                width: '100%',
                display: 'block',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
              }}
            >
              {errors}
            </FormHelperText>
          )
        )}
      </>
    );
  },
);
